import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
export const useAuthStore = defineStore('auth', () => {
    // 状态
    const user = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    // 计算属性
    const isAuthenticated = computed(() => !!user.value);
    const isAdmin = computed(() => user.value?.is_admin || false);
    const userDisplayName = computed(() => user.value?.display_name || user.value?.username || '');
    const userAvatar = computed(() => user.value?.avatar || '');
    // 权限相关计算属性
    const canAccessSystem = computed(() => user.value?.permissions?.can_access_system || false);
    const canEditMatchDetails = computed(() => user.value?.permissions?.can_edit_match_details || false);
    const canEditAllMatchInfo = computed(() => user.value?.permissions?.can_edit_all_match_info || false);
    const isCollegeUser = computed(() => user.value?.permissions?.is_college_user || false);
    const permissionLevel = computed(() => user.value?.permission_level || 'none');
    const userGroups = computed(() => user.value?.user_groups || []);
    // 获取API基础URL
    const getApiBaseURL = () => {
        if (process.env.NODE_ENV === 'production') {
            return '/api';
        }
        else {
            const protocol = window.location.protocol;
            const hostname = window.location.hostname;
            return `${protocol}//${hostname}:8000/api`;
        }
    };
    // 创建axios实例
    const api = axios.create({
        baseURL: getApiBaseURL(),
        timeout: 10000,
        withCredentials: true, // 重要：包含cookies用于session认证
        headers: {
            'Content-Type': 'application/json',
        }
    });
    // 请求拦截器 - 添加CSRF token
    api.interceptors.request.use((config) => {
        // 获取CSRF token
        const csrfToken = getCookie('csrftoken');
        if (csrfToken) {
            config.headers['X-CSRFToken'] = csrfToken;
        }
        return config;
    }, (error) => Promise.reject(error));
    // 获取cookie的辅助函数
    function getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()?.split(';').shift() || null;
        }
        return null;
    }
    // 响应拦截器
    api.interceptors.response.use((response) => response, (error) => {
        if (error.response?.status === 401) {
            // 认证失败，清除用户状态
            user.value = null;
            console.warn('认证失败，需要重新登录');
        }
        return Promise.reject(error);
    });
    // 获取CSRF token
    const fetchCsrfToken = async () => {
        try {
            const response = await api.get('/csrf-token/');
            return response.data.csrfToken;
        }
        catch (err) {
            console.warn('获取CSRF token失败:', err);
            return null;
        }
    };
    // 获取当前用户信息
    const fetchCurrentUser = async () => {
        try {
            isLoading.value = true;
            error.value = null;
            // 先获取CSRF token
            await fetchCsrfToken();
            const response = await api.get('/profile/');
            user.value = response.data;
            return user.value;
        }
        catch (err) {
            console.warn('获取用户信息失败:', err.response?.data || err.message);
            user.value = null;
            // 只有在非401错误时设置错误信息
            if (err.response?.status !== 401) {
                error.value = '获取用户信息失败';
            }
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    };
    // 登出
    const logout = async () => {
        try {
            isLoading.value = true;
            error.value = null;
            // 先获取CSRF token
            await fetchCsrfToken();
            // 先清除本地用户状态
            user.value = null;
            // 调用后端登出API
            const response = await api.post('/logout/');
            // 清除本地存储的认证信息
            localStorage.removeItem('auth_user');
            sessionStorage.removeItem('auth_user');
            // 手动清除所有可能的cookie
            document.cookie.split(";").forEach(cookie => {
                const eqPos = cookie.indexOf("=");
                const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
                if (name) {
                    // 清除当前域的cookie
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.zgcai.icu`;
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=zgcai.icu`;
                }
            });
            // 如果后端返回了OIDC登出URL，跳转到OIDC提供商登出
            if (response.data.logout_url) {
                window.location.href = response.data.logout_url;
            }
            else {
                // 否则跳转到登录页面
                window.location.href = '/login';
            }
        }
        catch (err) {
            console.error('登出失败:', err);
            error.value = '登出失败';
            // 即使登出失败，也清除本地状态
            user.value = null;
            localStorage.removeItem('auth_user');
            sessionStorage.removeItem('auth_user');
            // 尝试清除cookie
            document.cookie.split(";").forEach(cookie => {
                const eqPos = cookie.indexOf("=");
                const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
                if (name) {
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.zgcai.icu`;
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=zgcai.icu`;
                }
            });
            // 强制重新加载页面以确保清除所有状态
            window.location.href = '/login';
        }
        finally {
            isLoading.value = false;
        }
    };
    // 检查认证状态
    const checkAuth = async () => {
        try {
            await fetchCurrentUser();
            return true;
        }
        catch {
            return false;
        }
    };
    // 清除错误
    const clearError = () => {
        error.value = null;
    };
    // 重置状态
    const $reset = () => {
        user.value = null;
        isLoading.value = false;
        error.value = null;
    };
    return {
        // 状态
        user,
        isLoading,
        error,
        // 计算属性
        isAuthenticated,
        isAdmin,
        userDisplayName,
        userAvatar,
        // 权限相关计算属性
        canAccessSystem,
        canEditMatchDetails,
        canEditAllMatchInfo,
        isCollegeUser,
        permissionLevel,
        userGroups,
        // 方法
        fetchCurrentUser,
        logout,
        checkAuth,
        clearError,
        $reset
    };
});
